'use client';

import { useCallback, useMemo, useState } from 'react';
import { Globe } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '../lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../shadcn/select';

/** Sentinel used only inside the Select; the handler maps it back to `null`. */
const AUTOMATIC = '__automatic__';

export function LanguageSelector({
  onChange,
  selectedLocale,
  variant = 'inline',
  className,
}: {
  onChange?: (locale: string | null) => unknown;
  /**
   * The user's *explicit* choice, which only the server knows (it lives in the
   * `lang` cookie). `null` = Automatic. Without it the control can only show
   * the *resolved* language, so Automatic would render as e.g. "English" and
   * could never appear selected — the mobile picker shows "Automatic", and the
   * two surfaces must agree. Omit only where the cookie is genuinely
   * unreachable (Storybook).
   */
  selectedLocale?: string | null;
  variant?: 'inline' | 'footer' | 'header';
  className?: string;
}) {
  const { t, i18n } = useTranslation();
  const { language: currentLanguage, options } = i18n;

  const locales = (options.supportedLngs as string[]).filter(
    (locale) => locale.toLowerCase() !== 'cimode',
  );

  const languageNames = useMemo(() => {
    return new Intl.DisplayNames([currentLanguage], { type: 'language' });
  }, [currentLanguage]);

  const [value, setValue] = useState(
    selectedLocale === undefined
      ? i18n.language
      : (selectedLocale ?? AUTOMATIC),
  );

  const languageChanged = useCallback(
    async (selected: string) => {
      setValue(selected);

      const locale = selected === AUTOMATIC ? null : selected;

      if (onChange) {
        await onChange(locale);
      }

      if (locale) {
        await i18n.changeLanguage(locale);
      }

      // Server components rendered in the previous locale must re-render.
      window.location.reload();
    },
    [i18n, onChange],
  );

  return (
    <Select value={value} onValueChange={languageChanged}>
      <SelectTrigger
        className={cn(
          variant === 'footer' &&
            'text-muted-foreground hover:text-foreground h-8 w-auto gap-x-2 border-none bg-transparent px-2 text-sm shadow-none',
          // Icon-only trigger sized like the ghost ModeToggle button; the
          // chevron (last svg child) is hidden, the value stays sr-only.
          variant === 'header' &&
            'hover:bg-accent hover:text-accent-foreground h-9 w-9 justify-center gap-0 border-none bg-transparent p-0 shadow-none [&>svg:last-child]:hidden',
          className,
        )}
        aria-label={t('common:language.label')}
      >
        {variant === 'footer' || variant === 'header' ? (
          <Globe className="h-4 w-4" />
        ) : null}

        {variant === 'header' ? (
          <span className="sr-only">
            <SelectValue />
          </span>
        ) : (
          <SelectValue />
        )}
      </SelectTrigger>

      <SelectContent>
        <SelectItem value={AUTOMATIC}>
          {t('common:language.automatic')}
        </SelectItem>

        {locales.map((locale) => (
          <SelectItem value={locale} key={locale}>
            {capitalize(languageNames.of(locale) ?? locale)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function capitalize(lang: string) {
  return lang.slice(0, 1).toUpperCase() + lang.slice(1);
}
